import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Download, Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { formatBusinessType } from "@/lib/businessTypes";
import { downloadCsv } from "@/lib/csvExport";

const STATUS_STYLE = {
  pending: "text-amber-400 border-amber-400/40 bg-amber-500/10",
  active: "text-emerald-400 border-emerald-400/40 bg-emerald-500/10",
  suspended: "text-red-400 border-red-400/40 bg-red-500/10",
};

// Defensive: legacy vendors saved before the `status` field existed have
// status=null. Treat them as "pending" for display + activation purposes
// so the table never renders an empty badge.
const normalizeStatus = (raw) => {
  const s = (raw || "").toString().trim().toLowerCase();
  if (s === "active" || s === "pending" || s === "suspended") return s;
  return "pending";
};

export default function AdminVendorsPage({ superadmin = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.adminVendors();
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const activate = async (v) => {
    if (!window.confirm(`Activate ${v.business}?`)) return;
    try {
      await api.adminActivateVendor(v.id);
      toast.success("Vendor activated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const filtered = items.filter((v) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (v.business || "").toLowerCase().includes(s) ||
      (v.name || "").toLowerCase().includes(s) ||
      (v.email || "").toLowerCase().includes(s) ||
      (v.city || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Vendors</h1>
        <p className="text-muted-foreground text-sm">All vendor accounts on the platform.</p>
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative md:w-[360px] flex-1 min-w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search by business, email, city..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-vendors-search" />
          </div>
          {superadmin && (
            <Button
              variant="outline"
              onClick={() =>
                downloadCsv({
                  url: "/superadmin/vendors/export.csv",
                  params: q ? { q } : undefined,
                  filename: "citydooh-vendors.csv",
                })
              }
              data-testid="admin-vendors-export-csv"
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          )}
        </div>
      </Card>

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-vendors-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-secondary/40">
                  <th className="py-3 px-4">Business</th>
                  <th className="py-3 px-4">Owner</th>
                  <th className="py-3 px-4">City</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Phone</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Joined</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const status = normalizeStatus(v.status);
                  return (
                  <tr key={v.id} className="border-t border-border hover:bg-secondary/40" data-testid="admin-vendor-row">
                    <td className="py-3 px-4 font-medium">{v.business}</td>
                    <td className="py-3 px-4 text-muted-foreground">{v.name}</td>
                    <td className="py-3 px-4 text-muted-foreground">{v.city || "—"}</td>
                    <td className="py-3 px-4 text-muted-foreground" data-testid={`vendor-row-business-type-${v.id}`}>{formatBusinessType(v) || "—"}</td>
                    <td className="py-3 px-4 text-muted-foreground">{v.phone}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className={`capitalize ${STATUS_STYLE[status] || ""}`} data-testid="admin-vendor-status">{status}</Badge>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        {status === "pending" && (
                          <Button size="sm" onClick={() => activate(v)} data-testid="admin-activate-button">
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Activate
                          </Button>
                        )}
                        <Link to={`${superadmin ? "/superadmin" : "/admin"}/vendors/${v.id}`}>
                          <Button size="sm" variant="outline" data-testid="admin-vendor-view-button">
                            View <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td className="py-10 text-center text-muted-foreground" colSpan="8" data-testid="admin-vendors-empty">
                      No vendors found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
